import { getUrl } from 'helpers';
import styled, { css, keyframes } from 'styled-components';

export const Container = styled.div`
    align-items: center;
    bottom: 0;
    display: flex;
    justify-content: center;
    left: 0;
    padding: 12px;
    position: fixed;
    right: 0;
    top: 0;
    z-index: 10000;
`;

export const Backdrop = styled.div`
    background-color: rgba(0, 0, 0, 0.5);
    bottom: 0;
    left: 0;
    position: absolute;
    right: 0;
    top: 0;
    z-index: -1;
`;

export const Content = styled.div`
    background-color: #fff;
    border-radius: 8px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.18);
    max-height: calc(100dvh - 24px);
    max-width: 350px;
    overflow-y: auto;
    overscroll-behavior: contain;
    padding: 16px 16px 20px;
    position: relative;
    width: min(350px, calc(100vw - 24px));

    @supports not (height: 100dvh) {
        max-height: calc(100vh - 24px);
    }
`;

export const Form = styled.form<AuthModalFormProps>`
    display: flex;
    flex-direction: column;
    gap: 0;

    ${({ $loading }) =>
        $loading &&
        css`
            opacity: 0.5;
            pointer-events: none;
            user-select: none;
        `}
`;

export const CloseButton = styled.button.attrs({ type: 'button' })`
    background-color: rgba(0, 0, 0, 0);
    border-radius: 100%;
    border: 0;
    color: #deddd4;
    cursor: pointer;
    height: 24px;
    line-height: 1;
    padding: 0;
    position: absolute;
    right: 7px;
    top: 7px;
    transition: background-color 0.2s ease-in-out;
    width: 24px;
    z-index: 1;

    :active,
    :focus,
    :hover {
        :not(:disabled) {
            background-color: rgba(0, 0, 0, 0.04);
        }
    }
`;

export const Row = styled.div`
    align-items: center;
    display: flex;
    gap: 8px;
    justify-content: center;
    width: 100%;
`;

export const Section = styled.div<{
    $center?: boolean;
    $direction?: 'column' | 'row';
    $gap?: number;
    $mb?: number;
    $mt?: number;
}>`
    align-items: ${({ $center = false }) => ($center ? 'center' : 'stretch')};
    display: flex;
    flex-direction: ${({ $direction = 'column' }) => $direction};
    gap: ${({ $gap = 0 }) => `${$gap}px`};
    justify-content: ${({ $center = false }) => ($center ? 'center' : 'flex-start')};
    margin-bottom: ${({ $mb = 0 }) => `${$mb}px`};
    margin-top: ${({ $mt = 0 }) => `${$mt}px`};
    width: 100%;
`;

export const FieldsGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 20px;
    margin-bottom: 20px;
    width: 100%;
`;

export const SeparatorLine = styled.div`
    background-color: #deddd4;
    height: 1px;
    margin: 20px 12px;
    width: calc(100% - 24px);
`;

export const Title = styled.div`
    color: #171716;
    font-family: Montserrat, sans-serif;
    font-size: 24px;
    font-weight: 700;
    line-height: 32px;
    margin: 0;
    text-align: center;
`;

export const Text = styled.p<AuthModalTextProps>`
    color: ${({ $error }) => ($error ? '#d32f2f' : '#171716')};
    font-family: Roboto, sans-serif;
    font-size: ${({ $size }) => $size || 14}px;
    line-height: ${({ $size }) => {
        if (!$size) return '18px';
        if ($size <= 12) return '14px';
        if ($size >= 18) return '24px';
        return '18px';
    }};
    margin: 0;
    text-align: ${({ $center }) => ($center ? 'center' : 'inherit')};
`;

export const TextField = styled.input.attrs({ required: true })`
    border-radius: 8px;
    border: 1px solid #deddd4;
    font-family: Roboto, sans-serif;
    font-size: 14px;
    height: 40px;
    line-height: 1.333;
    margin: 0;
    padding: 0 20px;

    ::placeholder {
        color: #171716;
        opacity: 1;
    }

    :active,
    :focus,
    :hover {
        border-color: #171716;
        outline: 0;
    }
`;

export const CheckboxWrapper = styled.div<{ $compact?: boolean }>`
    align-items: center;
    cursor: pointer;
    display: flex;
    gap: 8px;

    > input {
        cursor: pointer;
    }

    > label {
        color: #171716;
        cursor: pointer;
        font-family: Roboto, sans-serif;
        font-size: ${({ $compact = false }) => ($compact ? '12px' : '14px')};
        font-weight: 400;
        line-height: ${({ $compact = false }) => ($compact ? '14px' : '18px')};
    }
`;

export const ModalDivider = styled.div`
    align-items: center;
    color: #5d5d5d;
    display: flex;
    font-family: Roboto, sans-serif;
    font-size: 12px;
    gap: 12px;
    justify-content: center;
    line-height: 1;
    margin: 0;
    width: 100%;

    :before,
    :after {
        background-color: #deddd4;
        content: '';
        flex: 1;
        height: 1px;
    }
`;

export const OAuthButton = styled.button`
    align-items: center;
    background-color: #fff;
    border-radius: 8px;
    border: 2px solid #171716;
    color: #171716;
    cursor: pointer;
    display: inline-flex;
    font-family: Roboto, sans-serif;
    font-size: 14px;
    font-weight: 700;
    gap: 8px;
    height: 48px;
    justify-content: center;
    line-height: 1.333;
    padding: 0 10px;
    width: 100%;

    :disabled {
        cursor: not-allowed;
        opacity: 0.6;
    }
`;

export const PrimaryButton = styled.button<{
    $tone?: 'dark' | 'yellow';
}>`
    align-items: center;
    background-color: ${({ $tone }) => ($tone === 'yellow' ? '#ffb100' : '#171716')};
    border: 0;
    border-radius: 28px;
    color: ${({ $tone }) => ($tone === 'yellow' ? '#171716' : '#fff')};
    cursor: pointer;
    display: inline-flex;
    font-family: Montserrat, sans-serif;
    font-size: 20px;
    font-weight: 700;
    height: 56px;
    justify-content: center;
    line-height: 1;
    padding: 0 32px;
    text-transform: none;
    width: 100%;

    :disabled {
        cursor: not-allowed;
        opacity: 0.6;
    }
`;

const authSwitchStyles = css`
    background-color: transparent;
    border: 0;
    color: #171716;
    cursor: pointer;
    font-family: Roboto, sans-serif;
    font-size: 14px;
    font-weight: 700;
    line-height: 1;
    min-width: 64px;
    padding: 0;
    position: relative;
    text-decoration: none;
    text-transform: none;
    transition: none;
    user-select: none;
    vertical-align: middle;

    :after {
        background-image: url(${getUrl('/assets/underline.svg')});
        background-repeat: no-repeat;
        background-size: 100%;
        bottom: 3px;
        content: '';
        height: 5px;
        left: -2px;
        position: absolute;
        right: -2px;
    }
`;

export const AuthSwitchButton = styled.button`
    ${authSwitchStyles}
`;

// Anchor variant used by the login view to link OUT to orca-front's dedicated
// password reset page (shares AuthSwitchButton's underline styling).
export const AuthSwitchLink = styled.a`
    ${authSwitchStyles}
    display: inline-block;
`;

export const OAuthIconWrap = styled.span`
    display: inline-flex;
    height: 20px;
    width: 20px;
`;

const loaderAnimation = keyframes`
    0% {
        transform: translate(-50%, -50%) rotate(0deg);
    }
    100% {
        transform: translate(-50%, -50%) rotate(360deg);
    }
`;

const loaderCircleAnimation = keyframes`
    0% {
        stroke-dasharray: 1px, 200px;
        stroke-dashoffset: 0;
    }
    50% {
        stroke-dasharray: 100px, 200px;
        stroke-dashoffset: -15px;
    }
    100% {
        stroke-dasharray: 100px, 200px;
        stroke-dashoffset: -125px;
    }
`;

export const Loader = styled.svg`
    animation: ${loaderAnimation} 1.4s linear infinite;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 2;
`;

export const LoaderCircle = styled.circle`
    animation: ${loaderCircleAnimation} 2s linear infinite;
    fill: none;
    stroke-dasharray: 80px, 200px;
    stroke-dashoffset: 0;
    stroke: currentColor;
    stroke-width: 4px;
`;

export const Notification = styled.div<AuthModalNotificationProps>`
    background-color: #43a048;
    border-radius: 8px;
    bottom: 0;
    color: #fff;
    left: 4px;
    max-width: 300px;
    padding: 12px 16px 12px 48px;
    position: absolute;
    transform: translateY(${({ $show }) => ($show ? 0 : 100)}%);
    transition: transform 0.3s ease-in-out;
    width: calc(100% - 8px);

    :before {
        background-color: #fff;
        border-radius: 100%;
        color: #43a048;
        content: '✓';
        font-weight: 700;
        height: 20px;
        left: 16px;
        line-height: 1;
        margin-right: 8px;
        padding: 1px;
        position: absolute;
        text-align: center;
        top: 14px;
        width: 20px;
    }
`;
